/**
 * Local viewpoint persistence (per-browser).
 *
 * Viewpoints carry the BCF-shaped Viewpoint payload (camera, section,
 * selection by GlobalId) plus a JPEG thumbnail Blob and a snapshot of
 * the per-element visibility / transparency state that BCF does not
 * cover. Everything lives in IndexedDB so the binary thumbnails do
 * not gnaw at the 5 MB localStorage budget.
 *
 * Why local-first: the viewer base is local-first by charter: IFC
 * bytes never leave the user's machine, so viewpoints (which are
 * derivative data of those bytes) shouldn't either. When the BCF
 * module gates on for a workspace, viewpoints relevant to a topic
 * promote to server storage; the local store stays as the user's
 * personal scratchpad.
 *
 * Indices:
 *  - byPrimaryHash: list viewpoints captured against a given IFC.
 *  - byCreatedAt: chronological listing.
 */
import type { Viewpoint } from './viewpoint'
import { markWorkDirty } from '@core/session/recovery'

const DB_NAME = 'vn2r.viewpoints'
const DB_VERSION = 1
const STORE = 'viewpoints'

export interface ViewpointStateSnapshot {
  hidden: { sha256: string; localIds: number[] }[]
  transparent: { sha256: string; localIds: number[] }[]
  ghostMode: boolean
  ghostOpacity: number
  transparencyOpacity: number
}

export interface LocalViewpoint {
  id: string
  name: string
  notes: string
  createdAt: string
  updatedAt: string
  /** SHA-256 of whichever model was active at capture time. Indexed
   * so the panel can filter to "viewpoints relevant to the active
   * model". */
  primaryHash: string
  /** SHA-256s of every visible model at capture time. */
  modelHashes: string[]
  thumbnail: Blob | null
  viewpoint: Viewpoint
  state: ViewpointStateSnapshot
  /** World-space anchor used by the optional 3D BCF pin overlay. */
  anchor?: [number, number, number] | null
  /** Folder name for grouping in the panel. Null/undefined = root. We
   * deliberately keep folders as a derived dimension (a string field on
   * each viewpoint) instead of a separate object store: zero migration
   * pain, empty folders just disappear, rename = update-many. */
  folder?: string | null
  /** Legacy field from the cloud-BCF era (Onda 1-5). Ignored as of
   * Onda 6: viewpoints are now session-wide and can be attached to
   * any topic of any loaded BCF. Kept on the type so old IDB rows
   * deserialise without errors. New captures leave it null. */
  bcfProjectId?: string | null
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('byPrimaryHash', 'primaryHash', { unique: false })
        store.createIndex('byCreatedAt', 'createdAt', { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      dbPromise = null
      reject(req.error ?? new Error('IDB open failed'))
    }
  })
  return dbPromise
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const store = tx.objectStore(STORE)
    let result: T
    Promise.resolve(fn(store))
      .then((r) => {
        result = r as T
      })
      .catch(reject)
    tx.oncomplete = () => resolve(result)
    tx.onerror = () =>
      reject(tx.error ?? new Error('IDB transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('IDB transaction aborted'))
  })
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IDB request failed'))
  })
}

/** Coerce a stored row into the current `LocalViewpoint` shape so
 * legacy entries (e.g. attached-image viewpoints from before
 * 2026-05-07 that were saved with `state = { models, camera, … }`
 * by `bcfAttachImage` / `persistImportedViewpoints`) don't crash
 * panels that read `state.hidden.length` directly. Read-side fix:
 * cheap, runs once per row and only on mismatch. */
function normaliseRow(row: LocalViewpoint): LocalViewpoint {
  const s = row.state as unknown as Partial<ViewpointStateSnapshot> | null
  if (
    s &&
    Array.isArray(s.hidden) &&
    Array.isArray(s.transparent) &&
    typeof s.ghostMode === 'boolean' &&
    typeof s.ghostOpacity === 'number' &&
    typeof s.transparencyOpacity === 'number'
  ) {
    return row
  }
  return {
    ...row,
    state: {
      hidden: Array.isArray(s?.hidden) ? s!.hidden : [],
      transparent: Array.isArray(s?.transparent) ? s!.transparent : [],
      ghostMode: typeof s?.ghostMode === 'boolean' ? s!.ghostMode : false,
      ghostOpacity:
        typeof s?.ghostOpacity === 'number' ? s!.ghostOpacity : 0.15,
      transparencyOpacity:
        typeof s?.transparencyOpacity === 'number'
          ? s!.transparencyOpacity
          : 0.35,
    },
  }
}

export const viewpointsStore = {
  async save(record: LocalViewpoint): Promise<void> {
    await withStore('readwrite', (s) => reqToPromise(s.put(record)))
    markWorkDirty()
  },
  async update(
    id: string,
    patch: Partial<LocalViewpoint>,
  ): Promise<LocalViewpoint | null> {
    const out = await withStore('readwrite', async (s) => {
      const cur = (await reqToPromise(s.get(id))) as LocalViewpoint | undefined
      if (!cur) return null
      const next: LocalViewpoint = {
        ...cur,
        ...patch,
        updatedAt: new Date().toISOString(),
      }
      await reqToPromise(s.put(next))
      return next
    })
    if (out) markWorkDirty()
    return out
  },
  async get(id: string): Promise<LocalViewpoint | null> {
    return withStore('readonly', async (s) => {
      const v = (await reqToPromise(s.get(id))) as LocalViewpoint | undefined
      return v ? normaliseRow(v) : null
    })
  },
  async list(opts?: { primaryHash?: string }): Promise<LocalViewpoint[]> {
    return withStore('readonly', async (s) => {
      const all = (
        (await reqToPromise(s.getAll())) as LocalViewpoint[]
      ).map(normaliseRow)
      let out = all
      if (opts?.primaryHash) {
        out = all.filter((v) => v.primaryHash === opts.primaryHash)
      }
      // Sort by createdAt desc (most recent first).
      out.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      return out
    })
  },
  async remove(id: string): Promise<void> {
    await withStore('readwrite', (s) => reqToPromise(s.delete(id)))
    markWorkDirty()
  },
  async clearAll(): Promise<void> {
    await withStore('readwrite', (s) => reqToPromise(s.clear()))
  },
  async sizeBreakdown(): Promise<{
    count: number
    thumbnailBytes: number
    metaBytes: number
  }> {
    return withStore('readonly', async (s) => {
      const all = (await reqToPromise(s.getAll())) as LocalViewpoint[]
      let thumbnailBytes = 0
      let metaBytes = 0
      for (const v of all) {
        if (v.thumbnail) thumbnailBytes += v.thumbnail.size
        // Rough JSON size for viewpoint + state. UTF-16 string length
        // is a decent upper bound on stored bytes for small payloads.
        try {
          metaBytes +=
            JSON.stringify(v.viewpoint).length +
            JSON.stringify(v.state).length +
            (v.name?.length ?? 0) +
            (v.notes?.length ?? 0)
        } catch {
          /* ignore */
        }
      }
      return { count: all.length, thumbnailBytes, metaBytes }
    })
  },
}
